// PlatformPeekHost — the entity-peek host for the PLATFORM frame (/platform/*),
// the sysadmin tier's sibling of the project-scoped PeekHost (ADR-036 §2 /
// ADR-018 trust zones: platform entities have no project context, so they get
// their own small host rather than an optional-projectId branch in the shared
// one). Mounted once in PlatformLayout; any platform page opens a peek by
// writing `?peek=projects:<id>` via openPeek — never by rendering a peek
// component itself. Like PeekHost it owns the sheet (`PeekSheet`) and the
// entity's peek fills it, so a face swap never replays the entrance.

import type { ReactNode } from 'react';
import { useEntityPeek } from '../../hooks/useEntityPeek.js';
import { ProjectPeek } from '../../pages/platform/projects/ProjectPeek.js';
import { PeekSheet } from '../patterns/ContextPanel.js';

export function PlatformPeekHost() {
  const { peek, closePeek } = useEntityPeek();
  const face = peek ? peekFace(peek, closePeek) : null;
  return (
    <PeekSheet open={face !== null} onClose={closePeek}>
      {face}
    </PeekSheet>
  );
}

/** The open ref's face, or null for a ref no platform entity answers. */
function peekFace(peek: string, closePeek: () => void): ReactNode {
  const sep = peek.indexOf(':');
  if (sep < 0) return null;
  const collection = peek.slice(0, sep);
  const rawId = peek.slice(sep + 1);

  // Projects carry STRING ids (prj_…) — pass through.
  if (collection === 'projects') {
    if (!rawId) return null;
    return <ProjectPeek projectId={rawId} onClose={closePeek} />;
  }

  return null;
}
